import { useQuery } from '@tanstack/react-query';
import { api, type SheetSyncRun } from '../lib/api';

/**
 * Every recorded pull, preview included — unlike History → External Syncing,
 * which lists only the runs that actually did something.
 *
 * Clicking one opens its detail in the Preview view. Only the most recent runs
 * keep that detail (see RUNS_KEEPING_DETAIL in apps/api/src/sheets/run.ts), so
 * older rows say they can no longer be opened rather than looking clickable and
 * then answering 404.
 */
export function SheetRunHistory({
  onOpen,
  openRunId,
}: {
  onOpen: (runId: string) => void;
  openRunId: string | null;
}) {
  const { data: runs, isLoading, error } = useQuery({
    queryKey: ['sheet-history', 'with-previews'],
    queryFn: () => api.sheetHistory(true),
  });

  if (isLoading) return <div className="empty-state">Loading history…</div>;
  if (error) return <div className="alert error">{(error as Error).message}</div>;

  if (!runs || runs.length === 0) {
    return (
      <div className="empty-state">
        <p>No pull has run yet.</p>
        <p className="small">Previews and real pulls, scheduled or by hand, are all recorded here.</p>
      </div>
    );
  }

  return (
    <div className="table-wrap">
      <table className="grid">
        <thead>
          <tr>
            <th>Started</th>
            <th style={{ width: 90 }}>Kind</th>
            <th>Rows read</th>
            <th>Staged</th>
            <th>Unchanged</th>
            <th>Unmatched</th>
            <th>Collisions</th>
            <th>Triggered by</th>
            <th style={{ width: 90 }}>Outcome</th>
            <th style={{ width: 80 }} />
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => (
            <tr key={run.id} className={run.id === openRunId ? 'row-in-flight' : undefined}>
              <td className="small muted nowrap">{new Date(run.startedAt).toLocaleString()}</td>
              <td className="nowrap">
                {run.dryRun ? (
                  <span className="badge" title="Planned only — nothing was staged or written back">
                    preview
                  </span>
                ) : (
                  <span className="badge ok">applied</span>
                )}
              </td>
              <td>
                {run.rowsRead}
                {run.sweep === 'partial' && (
                  <span className="badge warn" title="A SKU filter restricted this run">
                    part
                  </span>
                )}
              </td>
              <td>{run.staged}</td>
              <td>{run.unchanged}</td>
              <td>{run.unmatched}</td>
              <td>{run.collisions}</td>
              <td className="small nowrap">{run.triggeredBy ?? <span className="muted">scheduled</span>}</td>
              <td className="nowrap">{outcomeBadge(run)}</td>
              <td>
                {run.hasDetail ? (
                  <button className="link small" onClick={() => onOpen(run.id)}>
                    Open
                  </button>
                ) : (
                  <span className="small muted" title="Detail is only kept for the most recent runs">
                    not kept
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function outcomeBadge(run: SheetSyncRun) {
  if (!run.finishedAt) return <span className="badge" title="Still running">running</span>;
  return run.ok ? <span className="badge ok">ok</span> : <span className="badge error">failed</span>;
}
